import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { Button, FilterSelect, FilterAsyncSelect, Pill, confirmColors } from '@/components/ui';
import { blockTradeStatusTone } from './blockTradeStatus';
import type { FilterOption } from '@/components/ui';
import { loadBrokerOptions, loadCompanyOptions } from '@/components/utils/filters';
import { useTableState } from '@/hooks/useTableState';
import { useScope } from '@/hooks/useScope';
import { WRITE_ORDERS_AND_RECOMMENDATIONS } from '@/constants/scopeCodes';
import { blockTradeApi } from '@/services/api';
import { formatCurrency, formatDate, formatQuantityInLakhs } from '@/utils/formatters';
import {
  BLOCK_TRADE_PARTY_TYPE_OPTIONS,
  BLOCK_TRADE_STATUS,
  BLOCK_TRADE_STATUS_OPTIONS,
  BlockTrade,
  BlockTradePartyType,
  BlockTradeStatus,
} from '@/types/blockTrade';
import styles from '@/components/utils/shared.module.css';
import BlockTradeFormModal from './BlockTradeFormModal';

const STATUS_FILTER_OPTIONS: FilterOption[] = BLOCK_TRADE_STATUS_OPTIONS.map(o => ({
  value: o.value.toString(),
  label: o.label,
}));
const PARTY_TYPE_FILTER_OPTIONS: FilterOption[] = BLOCK_TRADE_PARTY_TYPE_OPTIONS.map(o => ({
  value: o.value.toString(),
  label: o.label,
}));

interface ActionMenuProps {
  anchorEl: null | HTMLElement;
  blockTrade: BlockTrade | null;
  canWrite: boolean;
  busy: boolean;
  onClose: () => void;
  onView: (blockTrade: BlockTrade) => void;
  onEdit: (blockTrade: BlockTrade) => void;
  onExpire: (blockTrade: BlockTrade) => void;
  onDelete: (blockTrade: BlockTrade) => void;
}

const BlockTradeActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl, blockTrade, canWrite, busy, onClose, onView, onEdit, onExpire, onDelete,
}) => {
  if (!blockTrade) return null;
  const isExpired = blockTrade.status === BLOCK_TRADE_STATUS.EXPIRED;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      onClick={onClose}
      PaperProps={{
        elevation: 3,
        sx: {
          minWidth: 160,
          borderRadius: 2,
          mt: 1,
          '& .MuiMenuItem-root': { typography: 'body2', py: 1.25 },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={() => onView(blockTrade)}>
        <ListItemIcon>
          <VisibilityIcon fontSize="small" sx={{ color: 'var(--text-dim)' }} />
        </ListItemIcon>
        <ListItemText>View</ListItemText>
      </MenuItem>

      {canWrite && (
        <MenuItem onClick={() => onEdit(blockTrade)} disabled={busy}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: 'var(--accent)' }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      )}

      {canWrite && (
        <MenuItem onClick={() => onExpire(blockTrade)} disabled={busy || isExpired}>
          <ListItemIcon>
            <EventBusyIcon fontSize="small" sx={{ color: isExpired ? 'var(--text-faint)' : 'var(--warn)' }} />
          </ListItemIcon>
          <ListItemText>{isExpired ? 'Already expired' : 'Expire'}</ListItemText>
        </MenuItem>
      )}

      {canWrite && (
        <MenuItem onClick={() => onDelete(blockTrade)} disabled={busy}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'var(--neg)' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: 'var(--neg)' }}>Delete</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
};

/** Today in YYYY-MM-DD — the deal book opens on today's quotes. */
const today = (): string => new Date().toISOString().split('T')[0];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.detail || fallback;
  }
  return fallback;
};

const BlockTradeList: React.FC = () => {
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_ORDERS_AND_RECOMMENDATIONS);

  const [blockTrades, setBlockTrades] = useState<BlockTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingActionId, setPendingActionId] = useState<number | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>(
    BLOCK_TRADE_STATUS.AVAILABLE.toString(),
  );
  const [partyTypeFilter, setPartyTypeFilter] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<FilterOption | null>(null);
  const [brokerFilter, setBrokerFilter] = useState<FilterOption | null>(null);
  const [defaultDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(defaultDate);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BlockTrade | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuBlockTrade, setMenuBlockTrade] = useState<BlockTrade | null>(null);

  const {
    currentPage, pageSize, ordering, searchQuery, debouncedSearch,
    setTotalCount, onSort, onSearchChange, onPageChange, resetPage,
    totalCount, totalPages, sortField, sortDir,
  } = useTableState({ defaultSortField: 'trade_date', defaultSortDir: 'desc', searchDebounceMs: 400 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blockTradeApi.getBlockTrades({
        page: currentPage,
        page_size: pageSize,
        ordering,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter ? (Number(statusFilter) as BlockTradeStatus) : undefined,
        party_type: partyTypeFilter
          ? (Number(partyTypeFilter) as BlockTradePartyType)
          : undefined,
        company: companyFilter ? Number(companyFilter.value) : undefined,
        broker: brokerFilter ? Number(brokerFilter.value) : undefined,
        trade_date: selectedDate || undefined,
      });
      setBlockTrades(response.results);
      setTotalCount(response.count);
    } catch (err) {
      console.error('Error loading block trades:', err);
      setError(getErrorMessage(err, 'Failed to load block trades'));
    } finally {
      setLoading(false);
    }
  }, [
    currentPage, pageSize, ordering, debouncedSearch, statusFilter, partyTypeFilter,
    companyFilter, brokerFilter, selectedDate, setTotalCount,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, blockTrade: BlockTrade) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
      setMenuBlockTrade(blockTrade);
    },
    [],
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuBlockTrade(null);
  }, []);

  const handleEdit = useCallback((blockTrade: BlockTrade) => {
    setEditing(blockTrade);
    setModalOpen(true);
  }, []);

  const handleExpire = useCallback(async (blockTrade: BlockTrade) => {
    const confirmation = await Swal.fire({
      title: 'Expire this block?',
      html: `<b>${blockTrade.company_symbol}</b> — ${formatQuantityInLakhs(blockTrade.quantity, 2)} shares `
        + `@ ${formatCurrency(blockTrade.ask_price, undefined, 0)} via ${blockTrade.broker_name}`
        + '<br/><br/>It will stay in the list, marked Expired.',
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Yes, expire it',
    });
    if (!confirmation.isConfirmed) return;

    setPendingActionId(blockTrade.id);
    try {
      const expired = await blockTradeApi.expireBlockTrade(blockTrade.id);
      toast.success(`Block trade for ${expired.company_symbol} expired`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to expire block trade'));
    } finally {
      setPendingActionId(null);
    }
  }, [load]);

  const handleDelete = useCallback(async (blockTrade: BlockTrade) => {
    const confirmation = await Swal.fire({
      title: 'Delete this block trade?',
      text: `${blockTrade.company_symbol} via ${blockTrade.broker_name} will be removed from the deal book.`,
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Yes, delete it',
    });
    if (!confirmation.isConfirmed) return;

    setPendingActionId(blockTrade.id);
    try {
      await blockTradeApi.deleteBlockTrade(blockTrade.id);
      toast.success('Block trade deleted');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete block trade'));
    } finally {
      setPendingActionId(null);
    }
  }, [load]);

  const handleSaved = useCallback((saved: BlockTrade) => {
    setModalOpen(false);
    setEditing(null);
    toast.success(`Block trade for ${saved.company_symbol} saved`);
    load();
  }, [load]);

  const columns = useMemo((): ColumnDef<BlockTrade>[] => {
    const baseColumns: ColumnDef<BlockTrade>[] = [
      {
        key: 'trade_date',
        header: 'Date',
        sortable: true,
        render: r => formatDate(r.trade_date),
      },
      {
        key: 'company__symbol',
        header: 'Company',
        sortable: true,
        render: r => (
          <span title={r.company_name}>
            <strong>{r.company_symbol}</strong>
          </span>
        ),
      },
      {
        key: 'broker__broker_name',
        header: 'Broker',
        sortable: true,
        render: r => r.broker_name,
      },
      {
        key: 'party_type',
        header: 'Buyer / Seller',
        render: r => r.party_type_display,
      },
      {
        key: 'quantity',
        header: 'Qty (Lakh)',
        sortable: true,
        align: 'right',
        // Bare — the header already carries the unit, so the rows stay a clean column of numbers.
        render: r => formatQuantityInLakhs(r.quantity, 2, { bare: true }),
      },
      {
        key: 'ask_price',
        // The quote is named for the side it is offered on, and a deal book mixes both,
        // so the value carries the name rather than the header.
        header: 'Bid / Ask Price',
        sortable: true,
        align: 'right',
        render: r => (
          <span title={r.quoted_price_label}>{formatCurrency(r.ask_price, undefined, 0)}</span>
        ),
      },
      {
        key: 'investment_strategies',
        header: 'Strategies',
        // A block with none predates the rule that at least one must be chosen; its
        // figures cover the whole firm, so it is flagged rather than left blank.
        render: r => {
          const strategies = r.investment_strategy_details;
          if (strategies.length === 0) {
            return <span title="Not set — figures cover the whole firm">—</span>;
          }
          const codes: string[] = [];
          const names: string[] = [];
          for (const s of strategies) {
            codes.push(s.code);
            names.push(s.name);
          }
          return <span title={codes.join(', ')}>{names.join(', ')}</span>;
        },
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: r => <Pill tone={blockTradeStatusTone(r.status)}>{r.status_display}</Pill>,
      },
    ];

    // A kebab menu rather than a row of buttons — the actions live in the menu, so the column
    // stays narrow and the row height does not depend on how many actions a user can take.
    return [
      ...baseColumns,
      {
        key: 'actions',
        header: 'Actions',
        render: r => (
          <IconButton
            size="small"
            aria-label={`Actions for ${r.company_symbol}`}
            onClick={e => handleOpenMenu(e, r)}
            disabled={pendingActionId === r.id}
            sx={{ color: 'var(--text-faint)' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [pendingActionId, handleOpenMenu]);

  const hasActiveFilters = !!(
    companyFilter || brokerFilter || partyTypeFilter || searchQuery.trim()
    || selectedDate !== defaultDate
    || statusFilter !== BLOCK_TRADE_STATUS.AVAILABLE.toString()
  );

  const handleClearFilters = () => {
    setStatusFilter(BLOCK_TRADE_STATUS.AVAILABLE.toString());
    setPartyTypeFilter('');
    setCompanyFilter(null);
    setBrokerFilter(null);
    setSelectedDate(defaultDate);
    onSearchChange('');
    resetPage();
  };

  return (
    <>
      <DataTable
        title="Block Trades"
        subtitle="Block orders and the allocations booked against them."
        headerActions={canWrite ? (
          <Button variant="primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
            + New Block Trade
          </Button>
        ) : undefined}
        data={blockTrades}
        columns={columns}
        rowKey={r => r.id}
        totalCount={totalCount}
        loading={loading}
        error={error}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        pageSize={pageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search by company or broker…"
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        emptyMessage="No block trades found for the selected date."
        onRowClick={r => navigate(`/block-trades/${r.id}`)}
        rowStyle={r => (
          r.status === BLOCK_TRADE_STATUS.EXPIRED
            ? { opacity: 0.65, backgroundColor: 'var(--surface-2)' }
            : undefined
        )}
        filterSlot={
          <>
            {/* A plain date input, not LastWorkingDateFilter — a block quote is dated the
                day the broker gave it, so the deal book opens on today, not the last
                working day. */}
            <div className={styles['filter-group']}>
              <label htmlFor="block-trade-date-filter">Date</label>
              <input
                id="block-trade-date-filter"
                type="date"
                className={styles.input}
                value={selectedDate}
                onChange={e => { setSelectedDate(e.target.value); resetPage(); }}
              />
            </div>

            <FilterSelect
              label="Status"
              value={STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter) ?? null}
              onChange={opt => { setStatusFilter(opt?.value ?? ''); resetPage(); }}
              options={STATUS_FILTER_OPTIONS}
            />

            <FilterSelect
              label="Buyer / Seller"
              value={PARTY_TYPE_FILTER_OPTIONS.find(o => o.value === partyTypeFilter) ?? null}
              onChange={opt => { setPartyTypeFilter(opt?.value ?? ''); resetPage(); }}
              options={PARTY_TYPE_FILTER_OPTIONS}
            />

            <FilterAsyncSelect
              label="Company"
              value={companyFilter}
              onChange={option => { setCompanyFilter(option); resetPage(); }}
              loadOptions={loadCompanyOptions}
              placeholder="All Companies"
            />

            <FilterAsyncSelect
              label="Broker"
              value={brokerFilter}
              onChange={option => { setBrokerFilter(option); resetPage(); }}
              loadOptions={loadBrokerOptions}
              placeholder="All Brokers"
            />
          </>
        }
      />

      <BlockTradeActionMenu
        anchorEl={menuAnchorEl}
        blockTrade={menuBlockTrade}
        canWrite={canWrite}
        busy={menuBlockTrade !== null && pendingActionId === menuBlockTrade.id}
        onClose={handleCloseMenu}
        onView={r => navigate(`/block-trades/${r.id}`)}
        onEdit={handleEdit}
        onExpire={handleExpire}
        onDelete={handleDelete}
      />

      <BlockTradeFormModal
        open={modalOpen}
        blockTrade={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={handleSaved}
      />
    </>
  );
};

export default BlockTradeList;
